import { useEffect, useState } from 'react'
import { Image, type ViewStyle } from 'react-native'
import { SvgCss } from 'react-native-svg/css'

import { Box, useTheme } from '../theme/restyle'

/**
 * Fun's CDN chain logos are inconsistent: some ship `width`/`height` with **no
 * `viewBox`** (e.g. the 28×28 EVM coins — these don't scale unless we add one),
 * while others ship their own `viewBox` at a different scale/origin (e.g. Solana
 * 16×16, Tron `138.8 49.5 522 500.2`). A blanket `viewBox` override therefore
 * breaks the latter — squashing Solana into a corner and pushing Tron entirely
 * off-screen. So we fetch the SVG and **synthesize a `viewBox` only when one is
 * missing** (derived from width/height), never clobbering an existing one, then
 * render with {@link SvgCss}. Results are cached per URL.
 */
const svgCache = new Map<string, string>()

function ensureViewBox(svg: string): string {
  if (/\bviewBox\s*=/.test(svg)) {
    return svg
  }
  const width = svg.match(/\bwidth="([\d.]+)"/)?.[1]
  const height = svg.match(/\bheight="([\d.]+)"/)?.[1]
  if (!width || !height) {
    return svg
  }
  return svg.replace(/<svg\b/, `<svg viewBox="0 0 ${width} ${height}"`)
}

/**
 * Renders a Fun CDN icon. SVG URLs (chain logos) are fetched, viewBox-normalized
 * and drawn via `SvgCss` (RN's built-in `Image` can't render SVG); raster URLs
 * (token PNGs) go through `Image`. Clipped to a circle.
 *
 * `SvgCss` over `SvgXml` because many CDN logos style their paths only through a
 * `<style>` block, which `SvgXml` drops — leaving them to paint black.
 */
export interface FunIconProps {
  uri: string
  size?: number
  /**
   * Raster URL to swap in if `uri` fails to load (SVG fetch error or `<Image>`
   * `onError`). Mirrors web's `AsyncImage` `fallbackSrc` — the token list passes
   * `FALLBACK_ASSET_LOGO` (dollar-circle) so a dead CDN URL degrades to the same
   * icon web shows, rather than a blank placeholder.
   */
  fallbackUri?: string
}

export function FunIcon({ uri, size = 32, fallbackUri }: FunIconProps) {
  // The URL we're actually rendering; on load failure we swap to `fallbackUri`
  // (once), matching web's `AsyncImage` fallback-on-error behavior.
  const [currentUri, setCurrentUri] = useState(uri)
  const isSvg = currentUri.endsWith('.svg')
  const [xml, setXml] = useState<string | null>(
    () => svgCache.get(currentUri) ?? null,
  )
  // Raster (Image) load state — drives the placeholder until the logo paints.
  const [rasterLoaded, setRasterLoaded] = useState(false)
  const theme = useTheme()

  // Reset when the requested icon changes (e.g. a different token row).
  useEffect(() => {
    setCurrentUri(uri)
    setRasterLoaded(false)
    setXml(svgCache.get(uri) ?? null)
  }, [uri])

  // Swap to the fallback once if the current URL can't be rendered.
  const onFailure = () => {
    if (fallbackUri && currentUri !== fallbackUri) {
      setCurrentUri(fallbackUri)
      setRasterLoaded(false)
      setXml(svgCache.get(fallbackUri) ?? null)
    }
  }

  useEffect(() => {
    if (!isSvg) {
      return
    }
    const cached = svgCache.get(currentUri)
    if (cached != null) {
      setXml(cached)
      return
    }
    let active = true
    fetch(currentUri)
      .then((res) => {
        // A 404 still resolves; its body is an error page, not SVG. Treat a
        // non-OK response as a failure so it falls back instead of rendering a
        // blank circle (e.g. a mistyped CDN chain key like `bnb` vs `bsc`).
        if (!res.ok) {
          throw new Error(`icon fetch ${res.status}`)
        }
        return res.text()
      })
      .then((text) => {
        if (!text.includes('<svg')) {
          throw new Error('icon response is not an SVG')
        }
        const normalized = ensureViewBox(text)
        svgCache.set(currentUri, normalized)
        if (active) {
          setXml(normalized)
        }
      })
      .catch(() => {
        // Fetch failed: fall back to the dollar-circle (like web) if one was
        // supplied; otherwise the placeholder fill stays visible.
        if (active) {
          onFailure()
        }
      })
    return () => {
      active = false
    }
  }, [currentUri, isSvg])

  // Show a neutral placeholder circle until the icon is ready — i.e. while the
  // SVG is being fetched (or its fetch failed → `xml` stays null) or while the
  // raster logo hasn't painted (or 404'd → `onLoad` never fires). Avoids the
  // bare/broken icon gap seen on slow networks or missing CDN assets.
  const ready = isSvg ? xml != null : rasterLoaded
  const clip: ViewStyle = { overflow: 'hidden' }
  return (
    <Box
      width={size}
      height={size}
      borderRadius="rounded"
      style={[
        clip,
        { backgroundColor: ready ? undefined : theme.background.secondary },
      ]}
    >
      {isSvg ? (
        xml != null ? (
          <SvgCss xml={xml} width={size} height={size} />
        ) : null
      ) : (
        <Image
          source={{ uri: currentUri }}
          style={{ width: size, height: size }}
          onLoad={() => setRasterLoaded(true)}
          onError={onFailure}
        />
      )}
    </Box>
  )
}
